import {
  faCalendarCheck,
  faShop,
  faSmog,
  faDollarSign,
  faBox,
  faTriangleExclamation,
  faCircleCheck,
  faPause,
  faSpinner
} from "@fortawesome/free-solid-svg-icons";

import { mainTheme } from "@aclymatepackages/themes";

import {
  faPodiumStar,
  faQuestion,
  faPiggyBank,
  faMeterBolt,
  faFileArrowUp
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";

import { bomProductsFieldOptions } from "./products";

export const transactionsFieldOptions = [
  {
    label: "Date",
    value: "date",
    description: "When did this transaction happen?",
    icon: faCalendarCheck,
    required: true,
    type: "string"
  },
  {
    label: "Vendor",
    value: "vendor",
    description: "Who did you buy it from?",
    icon: faShop,
    required: true,
    type: "string"
  },
  {
    label: "Amount",
    value: "amount",
    description: "How much did it cost?",
    icon: faDollarSign,
    required: true,
    type: "number"
  },
  {
    label: "Category",
    value: "category",
    description: "(Optional) What type of purchase was it?",
    icon: faSmog,
    required: false,
    type: "string"
  }
];

export const shippingFieldOptions = [
  { label: "Date", value: "date", type: "string" },
  { label: "Weight", value: "weight", type: "number" },
  { label: "Weight Units", value: "weightUnits", type: "string" },
  { label: "Sender Postal Code", value: "senderPostalCode", type: "string" },
  {
    label: "Recipient Postal Code",
    value: "recipientPostalCode",
    type: "string"
  },
  { label: "Recipient Country", value: "recipientCountry", type: "string" },
  { label: "Service Type", value: "serviceType", type: "string" },
  { label: "Miles", value: "miles", type: "number" },
  { label: "Kilometers", value: "kilometers", type: "number" },
  { label: "Direction", value: "direction", type: "string" }
];

// required flags mirror components/hooks/eventAttendeesCsvUploader.js's
// requiredFields -- only city/state/country/zipCode are mandatory for this
// upload; transportationMethod is optional and can legitimately be absent.
export const eventAttendeesFieldOptions = [
  { label: "City", value: "city", required: true, type: "string" },
  { label: "State", value: "state", required: true, type: "string" },
  { label: "Zip Code", value: "zipCode", required: true, type: "string" },
  { label: "Country", value: "country", required: true, type: "string" },
  {
    label: "Transportation Method",
    value: "transportationMethod",
    required: false,
    type: "string"
  }
];

export const waterCsvFieldOptions = [
  { label: "Date", value: "dateCommon", type: "string" },
  { label: "Quantity", value: "quantity", type: "number" },
  { label: "Unit", value: "unit", type: "string" },
  { label: "Wastewater %", value: "wastewaterPercentage", type: "number" }
];

export const steamCsvFieldOptions = [
  { label: "Date", value: "dateCommon", type: "string" },
  { label: "Quantity", value: "quantity", type: "number" },
  { label: "Unit", value: "unit", type: "string" }
];

export const chillWaterCsvFieldOptions = [
  { label: "Date", value: "dateCommon", type: "string" },
  { label: "kWh", value: "kwh", type: "number" }
];

export const refrigerantsCsvFieldOptions = [
  { label: "Date", value: "dateCommon", type: "string" },
  { label: "Quantity", value: "quantity", type: "number" },
  { label: "Unit", value: "unit", type: "string" },
  { label: "Refrigerant Type", value: "refrigerantType", type: "string" }
];

export const getFieldOptionsByTaskType = (taskType) => {
  if (taskType === "vendors" || taskType === "accounting") {
    return transactionsFieldOptions;
  }
  if (taskType === "shipping-transactions") {
    return shippingFieldOptions;
  }
  if (taskType === "event-attendees-csv") {
    return eventAttendeesFieldOptions;
  }
  if (taskType === "bom-products") {
    return bomProductsFieldOptions;
  }
  if (taskType === "purchased-water-csv") {
    return waterCsvFieldOptions;
  }
  if (taskType === "steam-csv") {
    return steamCsvFieldOptions;
  }
  if (taskType === "chill-water-csv") {
    return chillWaterCsvFieldOptions;
  }
  if (taskType === "refrigerants-csv") {
    return refrigerantsCsvFieldOptions;
  }
  return [];
};

export const getExpectedTypeForField = (fieldOptions, field) => {
  const found = fieldOptions.find((opt) => opt.value === field);

  return found?.type;
};

export const isFieldRequired = (fieldOptions, field) => {
  const found = fieldOptions.find((opt) => opt.value === field);

  return found?.required ?? true;
};

export const isValueValidForType = (value, type, required = true) => {
  if (!required && (value === null || value === undefined)) {
    return true;
  }
  if (type === "number") {
    return value === "" || !isNaN(Number(value));
  }
  if (type === "string") {
    return typeof value === "string";
  }
  return true;
};

export const getTypeErrorMessage = (type) => {
  if (type === "number") {
    return "Expected a numeric value.";
  }
  if (type === "string") {
    return "Expected a string value.";
  }
  return undefined;
};

export const getTaskTypesDetails = ({ taskType, fontSize = "small" }) => {
  const { palette } = mainTheme;

  switch (taskType) {
    case "transactions":
      return {
        name: "Transactions",
        Icon: <PlaylistAddCheckIcon fontSize={fontSize} color="white" />,
        color: palette["spend-based"].main
      };

    case "shipping-transactions":
      return {
        name: "Shipping Transactions",
        Icon: (
          <LocalShippingIcon fontSize={fontSize} style={{ color: "white" }} />
        ),
        color: palette.shipping.main
      };

    case "event-attendees-csv":
      return {
        name: "Event Attendees",
        Icon: (
          <FontAwesomeIcon
            icon={faPodiumStar}
            fontSize={fontSize}
            color="white"
          />
        ),
        color: palette.events.main
      };

    case "plaid-transactions":
      return {
        name: "Plaid Transactions",
        Icon: (
          <FontAwesomeIcon
            icon={faPiggyBank}
            fontSize={fontSize}
            color="white"
          />
        ),
        color: palette["spend-based"].dark
      };

    case "utilities-api":
      return {
        name: "Utilities API",
        Icon: (
          <FontAwesomeIcon
            icon={faMeterBolt}
            fontSize={fontSize}
            color="white"
          />
        ),
        color: palette.utilities.main
      };

    case "bom-products":
      return {
        name: "Supplier Products",
        Icon: (
          <FontAwesomeIcon icon={faBox} fontSize={fontSize} color="white" />
        ),
        color: palette.primary.main
      };

    case "purchased-water-csv":
      return {
        name: "Purchased Water CSV",
        Icon: (
          <FontAwesomeIcon
            icon={faFileArrowUp}
            fontSize={fontSize}
            color="white"
          />
        ),
        color: palette["purchased-water"].main
      };

    case "steam-csv":
      return {
        name: "Steam CSV",
        Icon: (
          <FontAwesomeIcon
            icon={faFileArrowUp}
            fontSize={fontSize}
            color="white"
          />
        ),
        color: palette.steam.main
      };

    case "chill-water-csv":
      return {
        name: "Chill Water CSV",
        Icon: (
          <FontAwesomeIcon
            icon={faFileArrowUp}
            fontSize={fontSize}
            color="white"
          />
        ),
        color: palette["chill-water"].main
      };

    case "refrigerants-csv":
      return {
        name: "Refrigerants CSV",
        Icon: (
          <FontAwesomeIcon
            icon={faFileArrowUp}
            fontSize={fontSize}
            color="white"
          />
        ),
        color: palette.refrigerants.main
      };

    default:
      return {
        name: "Unknown Task Type",
        Icon: (
          <FontAwesomeIcon
            icon={faQuestion}
            fontSize={fontSize}
            color="white"
          />
        ),
        color: palette.secondary.grayscale
      };
  }
};

export const formatTimestamp = (timestamp) => {
  if (!timestamp) {
    return "";
  }

  const date = timestamp?.seconds
    ? new Date(timestamp.seconds * 1000)
    : new Date(timestamp);

  if (isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat("default", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true
  }).format(date);
};

export const getTaskStatusDetails = (
  { status, totalTasks, totalTasksProcessed },
  numErroredTaskDataRows,
  palette
) => {
  if (status === "finished" && !!numErroredTaskDataRows) {
    return {
      icon: faTriangleExclamation,
      color: palette.error.main,
      summary: `We processed ${totalTasks} rows of data but were unable to process ${numErroredTaskDataRows} rows of data.`,
      spin: false
    };
  }

  if (status === "finished") {
    return {
      icon: faCircleCheck,
      color: palette.secondary.main,
      summary: `We processed ${totalTasks} rows of data without any errors`,
      spin: false
    };
  }

  if (status === "paused") {
    return {
      icon: faPause,
      color: palette.secondary.grayscale,
      summary: "Waiting on another process to complete before continuing.",
      spin: false
    };
  }

  if (status === "running") {
    return {
      icon: faSpinner,
      color: palette.primary.main,
      summary: `Processing data (${totalTasksProcessed ?? 0}/${totalTasks ?? 0})`,
      spin: true
    };
  }

  if (status === "uploading") {
    return {
      icon: faSpinner,
      color: palette.primary.main,
      summary: "Uploading necessary data before starting process",
      spin: true
    };
  }

  return {
    icon: faQuestion,
    color: palette.secondary.grayscale,
    summary: "Unknown status type",
    spin: false
  };
};

export const getFileNameFromPath = (filePath, type) => {
  if (!filePath || typeof filePath !== "string") {
    return "This task has no file path";
  }

  const parts = filePath.split("/");

  return parts[
    parts.length -
      ([
        "transactions",
        "shipping-transactions",
        "event-attendees-csv"
      ].includes(type)
        ? 2
        : 1)
  ];
};
